/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "react-query";
import axios from "axios";
import { SetStateAction, useEffect, useState } from "react";
import "./admin.scss";
import LoadError from "../components/error";
import { ApiEndpoint } from "../components/utils";
import {
  Autocomplete,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import "react-circular-progressbar/dist/styles.css";
import { BrandButton } from "../components/form/buttons";
import NetworkError from "../components/networkError";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ExamProgressData, ExamRecord } from "../components/types";
import Loader from "../components/loader";

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}

interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}

interface PlaceType {
  description?: string;
  structured_formatting?: StructuredFormatting;
  id: number;
  label: string;
  examId: string;
  examName: string;
}

interface UserData {
  token?: string;
  userid?: number;
  isadmin?: boolean;
  istutor?: boolean;
  admintoken?: string;
}

interface SectionData {
  correctAnswers: number;
  incorrectAnswers: number;
  nonAnswered: number;
  score: number;
}

interface Snapshot {
  id_satsh: number;
  exam_id: number;
  userID: number;
  created_at: string;
}

const Admin = () => {
  const userData: UserData = {};
  const [selectedUser, setSelectedUser] = useState<PlaceType | null | any>(
    null
  );
  const [sectionId, setSectionId] = useState<number>();
  const [time, setTime] = useState(0);
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const [doSearch, setDoSearch] = useState<boolean>(false);
  const [examOptions, setExamOptions] = useState<readonly PlaceType[]>([]);
  const [selectedExam, setSelectedExam] = useState<PlaceType | null | any>(
    null
  );
  const [examData, setExamData] = useState<ExamProgressData | null>();
  const [sectionData, setSectionData] = useState<SectionData>();
  const [loadingSectionData, setLoadingSectionData] = useState(false);
  const [satVerbalLevel, setSatVerbalLevel] = useState(0);
  const [satMathLevel, setSatMathLevel] = useState(0);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);

  const [queryParameters] = useSearchParams();
  const userid = queryParameters.get("id");
  const token = queryParameters.get("token");

  if (userid && token) {
    userData.token = token;
    userData.userid = parseInt(userid);
    userData.isadmin = true;
  }

  const sectionOptions = [
    {
      id: 0,
      name: "SAT Reading and Writing Module 1",
    },
    {
      id: 1,
      name: "SAT Reading and Writing Module 2",
    },
    {
      id: 2,
      name: "SAT Reading and Writing Module 2b",
    },
    {
      id: 3,
      name: "SAT Math Module 1",
    },
    {
      id: 4,
      name: "SAT Math Module 2",
    },
    {
      id: 5,
      name: "SAT Math Module 2b",
    },
  ];

  const { error, refetch } = useQuery(
    `examAdminUserResultsPageData-${userData.userid}-${selectedExam?.examId}`,
    async () => {
      const resp = await axios.post(
        ApiEndpoint + "wp-json/satapp-api/v1/getExamData",
        {
          token: userData?.token,
          exam_id: selectedExam.examId,
          userID: userData.userid,
        }
      );

      if (!resp || !resp.data.success) {
        setHasError(true);
        return;
      }

      getSnapshots();
      setDoSearch(false);
      setSectionId(parseInt(resp.data.currentSectionId));
      setTime(resp.data.minutesRemaining);
      setSatVerbalLevel(
        resp.data.satVerbalLevel ? parseInt(resp.data.satVerbalLevel) : 0
      );
      setSatMathLevel(
        resp.data.satMathLevel ? parseInt(resp.data.satMathLevel) : 0
      );
      resp.data.examName = selectedExam.examName;
      setExamData(resp.data);
    },
    {
      enabled: doSearch,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const udpateData = async () => {
    await axios.post(`${ApiEndpoint}wp-json/satapp-api/v1/updateUserExam`, {
      token: userData?.token,
      exam_id: selectedExam.examId,
      userData: {
        currentSectionId: sectionId,
        lastQuestionAnswered: null,
        minutesRemaining: time,
        satVerbalLevel: satVerbalLevel,
        satMathLevel: satMathLevel,
      },
      userID: userData.userid,
    });
    setSectionId(0);
    setTime("" as any);
  };

  const getExamList = async () => {
    if (examOptions.length > 0) return;

    const resp = await axios.get(
      ApiEndpoint +
        "wp-json/satapp-api/v1/getExamNames?token=" +
        userData?.token
    );
    if (resp.data.exams.length > 0) {
      const list = resp.data.exams.map((item: ExamRecord) => {
        return {
          examId: item.examId,
          examName: item.examName,
          label: item.examName,
        };
      });
      setExamOptions(list);
    }
  };

  const getSectionIdData = async () => {
    if (!sectionId && sectionId !== 0) return;
    setLoadingSectionData(true);
    const resp = await axios.post(
      ApiEndpoint + "wp-json/satapp-api/v1/calculateSectionScore",
      {
        token: userData.token,
        sectionId: sectionId,
        userid: userData.userid,
        exam_id: selectedExam.examId,
      }
    );
    setSectionData(resp.data);
    setLoadingSectionData(false);
  };

  const getSnapshots = async () => {
    const resp = await axios.post(
      ApiEndpoint + "wp-json/satapp-api/v1/getSnapshots",
      {
        token: userData.token,
        exam_id: selectedExam.examId,
        user_id: userData.userid,
      }
    );
    setSnapshots(resp.data.results.reverse());
  };

  useEffect(() => {
    if (!doSearch) return;
    refetch();
  }, [doSearch]);

  useEffect(() => {
    getSectionIdData();
  }, [sectionId]);

  useEffect(() => {
    if (!selectedExam) {
      return;
    } else {
      setDoSearch(true);
    }
  }, [selectedExam]);

  useEffect(() => {
    if (userData) {
      getExamList();
    }
  }, [userData]);

  useEffect(() => {
    if (!userData.token && !userData.userid) {
      navigate("/");
    } else {
      setSelectedUser(userData.userid);
    }
  }, []);

  const stampToTime = (): string => {
    if (time) {
      const minutes: number = Math.floor(time / 60 / 1000) as number;
      const seconds: number = Math.floor((time / 1000) % 60) as number;

      return minutes + "m " + seconds + "s";
    } else {
      return "";
    }
  };

  if (error || (!userData?.isadmin && !userData?.istutor)) return <LoadError />;

  return (
    <div className="adminPage">
      <div className="pageHeader"></div>

      <div className="headline">
        <h1>User Admin Dashboard</h1>
      </div>
      <div className="pageContent results">
        <div style={{ margin: "40px" }}>
          {userData.isadmin && (
            <div className="userSwitcher">
              <Autocomplete
                disablePortal
                options={examOptions}
                value={selectedExam}
                sx={{ width: 300 }}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField {...params} label="choose exam" />
                )}
                onChange={(event: any, newValue: any) => {
                  setSelectedExam(newValue);
                }}
                noOptionsText="No exam chosen"
                getOptionLabel={(option: PlaceType) => option.label}
                disabled={!selectedUser}
              />
            </div>
          )}
        </div>

        {examData?.examComplete &&
          snapshots.map((item: Snapshot) => {
            return (
              <div className="resultsHolder">
                <BrandButton
                  onClick={() => {
                    navigate("/exam-results", {
                      replace: true,
                      state: {
                        snapshotID: item.id_satsh,
                        admin: true,
                        token: userData?.token,
                        userID: userData?.userid,
                        exam_id: selectedExam?.examId,
                        passedExamData: examData,
                      },
                    });
                  }}
                >
                  View Results For exam taken on {item.created_at}
                </BrandButton>
              </div>
            );
          })}

        {(examData?.examComplete || !examData?.examStarted) && selectedExam && (
          <div className="noResultsHolder">
            <p>
              No active exams for: <strong>{selectedExam?.examName}</strong>
            </p>
          </div>
        )}

        {!examData?.examComplete && examData?.examStarted && (
          <div
            className=""
            style={{
              display: selectedExam && selectedUser ? "flex" : "none",
              gap: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                margin: "0 60px 0 50px",
                width: "100%",
                borderTop: "1px solid #ccc",
                paddingTop: "30px",
              }}
            >
              <div className="" style={{ display: "flex", gap: "35px" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <strong>Current Section:</strong>{" "}
                  {(sectionId || sectionId === 0) && (
                    <Select
                      labelId="demo-simple-select-label"
                      id="section-select"
                      value={sectionId}
                      label="Current Section"
                      onChange={(evt: any) => setSectionId(evt.target.value)}
                    >
                      {sectionOptions.map((item) => {
                        return (
                          <MenuItem value={item.id}>
                            {item.id} - {item.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                  <div>
                    <br />
                    <strong>Current Section Stats:</strong>
                    {loadingSectionData ? (
                      <Loader />
                    ) : (
                      <ul>
                        <li>
                          <strong>Correct:</strong>{" "}
                          {sectionData ? sectionData.correctAnswers : ""}
                        </li>
                        <li>
                          <strong>Incorrect:</strong>{" "}
                          {sectionData ? sectionData.incorrectAnswers : ""}
                        </li>
                        <li>
                          <strong>Non-answered:</strong>{" "}
                          {sectionData ? sectionData.nonAnswered : ""}
                        </li>
                        <li>
                          <strong>Score:</strong>{" "}
                          {sectionData ? sectionData.score + "%" : ""}
                        </li>
                      </ul>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    flexDirection: "column",
                  }}
                >
                  <strong>Timing:</strong>{" "}
                  <TextField
                    id="time"
                    label="Minutes Remaining"
                    value={time / 60000}
                    onChange={(evt: any) =>
                      setTime(
                        (evt.target.value *
                          60000) as unknown as SetStateAction<number>
                      )
                    }
                  />
                  <p>{stampToTime()}</p>
                </div>
                <div>
                  <div>
                    <p>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={satVerbalLevel === 0 ? false : true}
                            onChange={(evt: any) =>
                              evt.target.checked
                                ? setSatVerbalLevel(1)
                                : setSatVerbalLevel(0)
                            }
                          />
                        }
                        label="SAT Verbal 2b "
                      />
                    </p>
                  </div>
                  <div>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={satMathLevel === 0 ? false : true}
                          onChange={(evt: any) =>
                            evt.target.checked
                              ? setSatMathLevel(1)
                              : setSatMathLevel(0)
                          }
                        />
                      }
                      label="SAT Math 2b"
                    />
                  </div>
                </div>
              </div>
              <div style={{ minWidth: "120px" }}>
                <BrandButton onClick={() => udpateData()}>
                  Update Data
                </BrandButton>
              </div>
            </div>
          </div>
        )}
      </div>
      <NetworkError open={hasError} />
    </div>
  );
};
export default Admin;
